import { useEffect, useRef } from "react";
import { revealEnabled } from "@/lib/reveal";
import Seo from "@/components/Seo";
import HeroSection from "@/components/home/HeroSection";
import TrustStripSection from "@/components/home/TrustStripSection";
import AudienceEntrySection from "@/components/home/AudienceEntrySection";
import PainSection from "@/components/home/PainSection";
import LeerlijnSection from "@/components/home/LeerlijnSection";
import PlatformShowcaseSection from "@/components/home/PlatformShowcaseSection";
import TeacherReportSection from "@/components/home/TeacherReportSection";
import ResultsSection from "@/components/home/ResultsSection";
import SocialProofSection from "@/components/home/SocialProofSection";
import FaqSection from "@/components/home/FaqSection";
import CtaSection from "@/components/home/CtaSection";
import StudentFlowSection from "@/components/home/StudentFlowSection";
import GrowingProfileSection from "@/components/home/GrowingProfileSection";
import ImplementationSection from "@/components/home/ImplementationSection";

const Home = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Reveal is desktop-only enhancement; content is visible by default (mobile, reduced motion, no JS).
    if (!revealEnabled || !("IntersectionObserver" in window)) return;
    const elements = document.querySelectorAll(".animate-on-scroll");
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px 15% 0px" }
    );
    elements.forEach((el) => observerRef.current?.observe(el));
    document.documentElement.classList.add("reveal-enabled");
    // Failsafe: never leave sections hidden.
    const failsafe = window.setTimeout(() => elements.forEach((el) => el.classList.add("is-visible")), 2500);
    return () => {
      window.clearTimeout(failsafe);
      observerRef.current?.disconnect();
      document.documentElement.classList.remove("reveal-enabled");
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="studie connect | Studiekeuze & LOB voor het voortgezet onderwijs"
        description="Doorlopende LOB voor het voortgezet onderwijs. Leerlingen bouwen stap voor stap aan zelfkennis, een LOB-dossier en een onderbouwde vervolgstap."
        path="/"
        keywords="LOB platform, LOB software, LOB voortgezet onderwijs, studiekeuze, doorlopende LOB-leerlijn, leerlingprofiel, LOB-dossier"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Vanaf welk leerjaar kun je met studie connect starten?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "studie connect kan vanaf het begin van de schoolloopbaan worden ingezet en groeit mee tot de vervolgstap. De school richt de leerlijn in op het eigen onderwijsniveau en programma.",
              },
            },
            {
              "@type": "Question",
              name: "Vervangt studie connect de mentor of decaan?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Nee. studie connect brengt informatie en voortgang samen, zodat de mentor of decaan met meer context het persoonlijke gesprek voert.",
              },
            },
            {
              "@type": "Question",
              name: "Welke rol speelt technologie in het advies?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Technologie helpt informatie uit opdrachten, reflecties en andere activiteiten samen te vatten en richtingen te verkennen. De leerling onderzoekt en beslist zelf.",
              },
            },
          ],
        }}
      />
      {/* 1. WHY — rustige hero met merkbelofte */}
      <HeroSection />
      {/* 1a. Trust-strip: sociale bewijskracht direct onder de hero */}
      <TrustStripSection />
      {/* 1b. Doelgroep-instap */}
      <AudienceEntrySection />
      {/* 2. "Wij begrijpen jouw uitdaging" — het probleem */}
      <PainSection />
      {/* 3. Het inzicht + doorlopende LOB-leerlijn */}
      <LeerlijnSection />
      {/* 4. Zo werkt het voor de leerling */}
      <StudentFlowSection />
      {/* 5. Wat het platform doet — interactieve tab-showcase */}
      <PlatformShowcaseSection />
      {/* 5b. Docentrapportage — voortgang van leerlingen */}
      <TeacherReportSection />
      {/* 6. Het resultaat — impact per rol */}
      <ResultsSection />
      <GrowingProfileSection />
      <ImplementationSection />
      {/* 7. Methodiek, grenzen en privacy */}
      <SocialProofSection />
      {/* 8. Veelgestelde vragen */}
      <FaqSection />
      {/* 9. Emotionele CTA */}
      <CtaSection />
    </div>
  );
};

export default Home;
